import React from 'react';
import { cva } from 'class-variance-authority';
import cn from './cn';

/**
 * The status pill.
 *
 * The CMS shows publish and import state as a bare coloured dot with no label, which is
 * unreadable to anyone who does not already know the convention and invisible to a screen
 * reader. This is the labelled version: a dot plus its name.
 *
 * It is new rather than a replacement, so nothing changes until a screen adopts it. The
 * colours are the ones the dots already use.
 */
export const badgeVariants = cva(
  [
    'tw-inline-flex tw-items-center tw-gap-1.5 tw-whitespace-nowrap tw-align-middle',
    'tw-rounded-pill tw-px-2 tw-py-0.5 tw-font-sans tw-text-caption tw-font-bold',
    'tw-border tw-border-solid',
  ],
  {
    variants: {
      variant: {
        published: 'tw-bg-white tw-border-success-dot tw-text-success',
        pending: 'tw-bg-white tw-border-pending tw-text-pending-text',
        unpublished: 'tw-bg-white tw-border-danger tw-text-danger',
        info: 'tw-bg-accent-surface tw-border-accent-border tw-text-accent-dark',
        neutral: 'tw-bg-fill tw-border-line-light tw-text-muted',
      },
    },
    defaultVariants: { variant: 'neutral' },
  }
);

const DOT = {
  published: 'tw-bg-success-dot',
  pending: 'tw-bg-pending',
  unpublished: 'tw-bg-danger',
  info: 'tw-bg-accent-dark',
  neutral: 'tw-bg-subtle',
};

const Badge = ({ variant = 'neutral', dot = true, className, children, ...props }) => (
  <span className={cn(badgeVariants({ variant }), className)} {...props}>
    {dot && (
      <span className={cn('tw-inline-block tw-h-1.5 tw-w-1.5 tw-rounded-pill', DOT[variant])} />
    )}
    {children}
  </span>
);

export default Badge;
